import React from 'react';
import { useHorarioDocente } from 'hooks/useHorarioDocente';
import { HorarioResponse, DiaSemana, DIA_LABEL, DIA_COLOR } from 'types/horario.types';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// HorarioDocentePage – Vista semanal del horario del docente
// ============================================================

const fmt = (t: string) => t?.substring(0, 5) ?? t;

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const DIAS_LECTIVOS: DiaSemana[] = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES'];

const HorarioDocentePage: React.FC = () => {
  const {
    horarios,
    añosEscolares,
    anioSeleccionado,
    setAnioSeleccionado,
    loading,
    error,
  } = useHorarioDocente();

  // Agrupar por día
  const porDia = DIAS_LECTIVOS.reduce<Record<DiaSemana, HorarioResponse[]>>((acc, dia) => {
    acc[dia] = horarios
      .filter(h => h.dia === dia)
      .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
    return acc;
  }, {} as Record<DiaSemana, HorarioResponse[]>);

  const diasExtra: DiaSemana[] = (['SABADO', 'DOMINGO'] as DiaSemana[]).filter(
    d => horarios.some(h => h.dia === d)
  );
  diasExtra.forEach(d => {
    porDia[d] = horarios
      .filter(h => h.dia === d)
      .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
  });

  const diasMostrar = [...DIAS_LECTIVOS, ...diasExtra];

  return (
    <div className="space-y-6">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Mi Horario</h2>
          <p className="text-slate-400 text-sm mt-0.5">
            Tus clases asignadas organizadas por día de la semana
          </p>
        </div>

        {/* Selector de año escolar */}
        <div className="flex items-center gap-2">
          <label htmlFor="anio" className="text-sm font-medium text-slate-600">Año escolar:</label>
          <select
            id="anio"
            value={anioSeleccionado ?? ''}
            onChange={e => setAnioSeleccionado(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            {añosEscolares.map(a => (
              <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Estado de carga / error */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-16">
          <SpinnerIcon />
          <span className="text-slate-500 text-sm">Cargando horario…</span>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && horarios.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-10 text-center">
          <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
          </svg>
          <p className="text-slate-500 font-medium">No tienes horarios asignados</p>
          <p className="text-slate-400 text-sm mt-1">Contacta al administrador para que configure tu horario.</p>
        </div>
      )}

      {/* Vista semanal */}
      {!loading && !error && horarios.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
          {diasMostrar.map(dia => (
            <div key={dia} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              {/* Cabecera del día */}
              <div className={`px-4 py-3 font-semibold text-sm ${DIA_COLOR[dia]}`}>
                {DIA_LABEL[dia]}
                <span className="ml-2 text-xs font-normal opacity-75">
                  ({porDia[dia].length} {porDia[dia].length === 1 ? 'clase' : 'clases'})
                </span>
              </div>

              {/* Clases del día */}
              <div className="p-3 space-y-2">
                {porDia[dia].length === 0 ? (
                  <p className="text-slate-300 text-xs text-center py-4">Sin clases</p>
                ) : (
                  porDia[dia].map(h => (
                    <div
                      key={h.idHorario}
                      className="bg-slate-50 rounded-xl p-3 border border-slate-100 hover:border-blue-200 transition-colors"
                    >
                      {/* Hora */}
                      <div className="flex items-center gap-1.5 text-blue-600 mb-1.5">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs font-bold">{fmt(h.horaInicio)} – {fmt(h.horaFin)}</span>
                      </div>

                      {/* Curso */}
                      <p className="text-slate-800 font-semibold text-sm leading-tight">{h.cursoNombre}</p>

                      {/* Grado, sección y nivel */}
                      <div className="flex items-center gap-1.5 mt-1.5 text-slate-400">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9.664 1.319a.75.75 0 0 1 .672 0 41.059 41.059 0 0 1 8.198 5.424.75.75 0 0 1-.254 1.285 31.372 31.372 0 0 0-7.86 3.83.75.75 0 0 1-.84 0 31.508 31.508 0 0 0-2.08-1.287V9.394c0-.244.116-.463.302-.592a35.504 35.504 0 0 1 3.305-2.033.75.75 0 0 0-.714-1.319 37 37 0 0 0-3.446 2.12A2.216 2.216 0 0 0 6 9.393v.38a31.293 31.293 0 0 0-4.28-1.746.75.75 0 0 1-.254-1.285 41.059 41.059 0 0 1 8.198-5.424ZM6 11.459a29.848 29.848 0 0 0-2.455-1.158 41.029 41.029 0 0 0-.39 3.114.75.75 0 0 0 .419.74c.528.256 1.046.53 1.554.82-.21.324-.455.63-.739.914a.75.75 0 1 0 1.06 1.06c.37-.369.69-.77.96-1.193a26.61 26.61 0 0 1 3.095 2.348.75.75 0 0 0 .992 0 26.547 26.547 0 0 1 5.93-3.95.75.75 0 0 0 .42-.739 41.053 41.053 0 0 0-.39-3.114 29.925 29.925 0 0 0-5.199 2.801.75.75 0 0 1-.84 0A29.926 29.926 0 0 0 6 11.46Z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs">{h.gradoNombre} — Sección {h.seccionNombre}</span>
                      </div>

                      {/* Badge de nivel */}
                      {h.gradoNivel && (
                        <span className={`inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded-full
                          ${NIVEL_COLOR[h.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                          {h.gradoNivel}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HorarioDocentePage;
